import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../../../components/buttons/Button';
import Table from '../../../components/tables/Table';
import Pagination from '../../../components/tables/pagination/Pagination';
import CustomLoading from '../../../components/customLoading/CustomLoading';
import SearchBar from '../../../components/searchBar/SearchBar';
import Dropdown from '../../../components/inputFields/DropDown';
import Breadcrumbs from '../../../components/BreadCrumbs';
import ShowModal from '../../../utils/ShowModal';
import { Formik, Form } from 'formik';
import InputField from '../../../components/inputFields/InputField';
import MultiSelectDropDown from '../../../components/inputFields/MultiSelectDropDown';
import MultiAsyncDropdown from '../../../components/inputFields/MultiAsyncDropDown';
import { searchProductWithAkaName } from '../../../services/search';
import B2BLeadUpload from './B2BLeadUpload';
import B2BLeadDetail from '../../../components/leads/b2b/B2BLeadDetail';
import { formatDateMonth } from '../../../utils/FormatDateTime';
import { BiFilterAlt, BiRefresh, BiNote, BiCodeAlt, BiPlus, BiX, BiSearch, BiEdit } from 'react-icons/bi';
import { FiUsers, FiDollarSign, FiFileText, FiUpload } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { Download, UserPlus, PhoneCall, Eye } from 'lucide-react';
import { getB2BLeads, getB2BCampaigns, getB2BLeadNotes, createB2BLeadNote, createB2BLead, updateB2BLead, getLeadStats, downloadB2BLeads, bulkAssignB2BLeads, getB2BSalespersonList } from '../../../services/b2bLeads';
import { statesOfIndia } from '../../../constants/states';
import axios from 'axios';
import { categoriesCRUDUrl, districtApi, subcategoriesCRUDUrl } from '../../../constants/apis';
import { getUserToken } from '../../../helpers/local_storage_helpers';
import { sanitizeMobile } from '../../../utils/phone';
import UserOrderHistory from '../../sales/UserOrderHistory';
import { normalizePaginatedResponse } from '../../../utils/pagination';

import { B2B_OUTCOME_MAP, B2B_OUTCOME_OPTIONS, B2B_PURPOSE_MAP } from '../../../constants/b2bWorkflow';
import B2BLogInteractionModal from '../../../components/leads/b2b/B2BLogInteractionModal';
import DateRangePicker from '../../../components/inputFields/DateRangePicker';

const isToday = (value) => {
    if (!value) return false;
    const date = new Date(value);
    const today = new Date();
    return date.toDateString() === today.toDateString();
};

const isOverdue = (value) => value && new Date(value).getTime() < Date.now();

const formatDateTime = (value) => value ? new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const outcomeBadge = (value = 'new') => {
    const item = B2B_OUTCOME_MAP[value] || B2B_OUTCOME_MAP.new;
    return (
        <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-black whitespace-nowrap ${item.badgeClass}`}>
            {item.label}
        </span>
    );
};

const B2BLeads = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ results: [], count: 0 });
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 30;
    const cancelTokenRef = useRef(null);

    // Filters
    const [campaigns, setCampaigns] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [showFilters, setShowFilters] = useState(false);

    const [filters, setFilters] = useState({
        search: '',
        campaign: searchParams.get('campaign') || '',
        state: '',
        district: searchParams.get('district') || '',
        area: '',
        product_category: '',
        product_subcategory: '',
        is_paid: '',
        sales_person: '',
        assigned: '',
        outcome: '',
        is_urgent: '',
        product: [],
        start_date: '',
        end_date: ''
    });

    // Modals & Drawers
    const [noteModal, setNoteModal] = useState({ open: false, lead: null, notes: [], newNote: '' });
    const [jsonModal, setJsonModal] = useState({ open: false, data: null });
    const [leadModal, setLeadModal] = useState({ open: false, mode: 'add', lead: null });
    const [uploadModal, setUploadModal] = useState(false);
    const [selectedLeadForDetail, setSelectedLeadForDetail] = useState(null);
    const [selectedLeadIds, setSelectedLeadIds] = useState([]);
    const [assignModal, setAssignModal] = useState({ open: false, leadIds: [] });
    const [assignForm, setAssignForm] = useState({ sales_person: '', remarks: '' });
    const [assigning, setAssigning] = useState(false);
    const [staffOptions, setStaffOptions] = useState([]);
    const [workflowCounts, setWorkflowCounts] = useState({ unassigned: 0, urgent: 0 });
    const [logModal, setLogModal] = useState({ open: false, lead: null });
    const [isUpdating, setIsUpdating] = useState(null);
    const [viewingCardUrl, setViewingCardUrl] = useState(null);
    const [modalVisitingCardFile, setModalVisitingCardFile] = useState(null);
    const [showOptionalDetails, setShowOptionalDetails] = useState(false);

    const handleVisitingCardUpload = async (leadId, file) => {
        if (!file) return;
        const toastId = toast.loading("Uploading visiting card...");
        try {
            const formData = new FormData();
            formData.append('visiting_card', file);
            const res = await updateB2BLead(leadId, formData);
            if (res.status === 200) {
                toast.update(toastId, { render: "Visiting card uploaded successfully", type: "success", isLoading: false, autoClose: 3000 });
                fetchLeads();
            } else {
                toast.update(toastId, { render: "Failed to upload visiting card", type: "error", isLoading: false, autoClose: 3000 });
            }
        } catch (error) {
            console.error("Error uploading visiting card:", error);
            toast.update(toastId, { render: "Failed to upload visiting card", type: "error", isLoading: false, autoClose: 3000 });
        }
    };
    const [stats, setStats] = useState([]);
    const [statsLoading, setStatsLoading] = useState(false);
    const [leadFormData, setLeadFormData] = useState({
        shop_name: '',
        owner_name: '',
        phone: '',
        additional_numbers: '',
        area: '',
        address_line1: '',
        state_name: '',
        district_name: '',
        product_category: [],
        product_subcategory: [],
        is_paid_dealer: false,
        is_urgent: false,
        campaign: ''
    });
    const [formDistricts, setFormDistricts] = useState([]);

    useEffect(() => {
        fetchInitialResources();
    }, []);

    useEffect(() => {
        fetchLeads();
        fetchStats();
    }, [currentPage, filters]);

    const fetchInitialResources = async () => {
        try {
            const [campRes, catRes, subCatRes] = await Promise.all([
                getB2BCampaigns(),
                axios.get(categoriesCRUDUrl, { headers: { Authorization: `Token ${getUserToken()}` } }),
                axios.get(`${subcategoriesCRUDUrl}?page_size=1000`, { headers: { Authorization: `Token ${getUserToken()}` } })
            ]);
            if (campRes.status === 200) setCampaigns(campRes.data.results || campRes.data);
            if (catRes.status === 200) setCategories(catRes.data.results || catRes.data);
            if (subCatRes.status === 200) setSubcategories(subCatRes.data.results || subCatRes.data);
        } catch (error) {
            console.error("Error fetching initial resources:", error);
        }
    };

    const fetchLeads = useCallback(async () => {
        if (cancelTokenRef.current) {
            cancelTokenRef.current.cancel();
        }
        cancelTokenRef.current = axios.CancelToken.source();

        setLoading(true);
        try {
            let query = `?page=${currentPage}&page_size=${pageSize}`;
            if (filters.search) query += `&search=${filters.search}`;
            if (filters.campaign) query += `&campaign=${filters.campaign}`;
            if (filters.state) query += `&state=${filters.state}`;
            if (filters.district) query += `&district=${filters.district}`;
            if (filters.area) query += `&area=${filters.area}`;
            if (filters.product_category) query += `&product_category=${filters.product_category}`;
            if (filters.product_subcategory) query += `&product_subcategory=${filters.product_subcategory}`;
            if (filters.is_paid !== '') query += `&is_paid=${filters.is_paid}`;
            if (filters.sales_person) query += `&sales_person=${filters.sales_person}`;
            if (filters.assigned !== '') query += `&assigned=${filters.assigned}`;
            if (filters.outcome) query += `&outcome=${filters.outcome}`;
            if (filters.is_urgent !== '') query += `&is_urgent=${filters.is_urgent}`;
            if (filters.product && filters.product.length > 0) {
                const productIds = filters.product.map(p => p.value).join(',');
                query += `&product=${productIds}`;
            }
            if (filters.start_date) query += `&start_date=${filters.start_date}`;
            if (filters.end_date) query += `&end_date=${filters.end_date}`;

            const res = await getB2BLeads(query);
            if (res.status === 200) {
                setData(normalizePaginatedResponse(res.data));
                setSelectedLeadIds([]);
            } else {
                toast.error("Failed to fetch leads");
            }
        } catch (error) {
            if (!axios.isCancel(error)) {
                toast.error("Error connecting to server");
            }
        } finally {
            setLoading(false);
        }
    }, [currentPage, filters]);

    const fetchStats = async () => {
        setStatsLoading(true);
        try {
            let query = "";
            if (filters.campaign) query += `?campaign=${filters.campaign}`;
            if (filters.state) query += (query ? "&" : "?") + `state=${filters.state}`;
            if (filters.district) query += (query ? "&" : "?") + `district=${filters.district}`;
            if (filters.start_date) query += (query ? "&" : "?") + `start_date=${filters.start_date}`;
            if (filters.end_date) query += (query ? "&" : "?") + `end_date=${filters.end_date}`;

            // Build a date suffix for the count-only calls
            let dateSuffix = "";
            if (filters.start_date) dateSuffix += `&start_date=${filters.start_date}`;
            if (filters.end_date) dateSuffix += `&end_date=${filters.end_date}`;

            const res = await getLeadStats(query);
            const [unassignedRes, urgentRes] = await Promise.all([
                getB2BLeads(`?assigned=false&page_size=1${dateSuffix}`),
                getB2BLeads(`?is_urgent=true&page_size=1${dateSuffix}`)
            ]);
            if (res.status === 200) {
                setStats(res.data.stats || []);
            }
            setWorkflowCounts({
                unassigned: unassignedRes?.status === 200 ? normalizePaginatedResponse(unassignedRes.data).count : 0,
                urgent: urgentRes?.status === 200 ? normalizePaginatedResponse(urgentRes.data).count : 0,
            });
        } catch (error) {
            console.error("Error fetching stats:", error);
        } finally {
            setStatsLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const handleStateChange = async (val) => {
        handleFilterChange('state', val);
        if (val) {
            try {
                const res = await axios.get(`${districtApi}${val}`, {
                    headers: { Authorization: `Token ${getUserToken()}` },
                });
                if (res.status === 200) setDistricts(res.data);
            } catch (error) {
                setDistricts([]);
            }
        } else {
            setDistricts([]);
        }
    };

    const resetFilters = () => {
        setFilters({ search: '', campaign: '', state: '', district: '', area: '', product_category: '', product_subcategory: '', is_paid: '', sales_person: '', assigned: '', outcome: '', is_urgent: '', product: [], start_date: '', end_date: '' });
        setSearchParams({});
        setCurrentPage(1);
    };

    const fetchSalespeople = async () => {
        try {
            const res = await getB2BSalespersonList();
            if (res.status === 200) {
                const list = res.data.results || res.data || [];
                setStaffOptions(list.map(user => ({
                    label: user.name || user.username || user.email,
                    value: user.id,
                })));
            }
        } catch (error) {
            console.error("Error fetching salespeople:", error);
        }
    };

    useEffect(() => {
        fetchSalespeople();
    }, []);

    const openNotes = async (lead) => {
        setNoteModal({ ...noteModal, open: true, lead: lead, loading: true });
        try {
            const res = await getB2BLeadNotes(lead.id);
            if (res.status === 200) {
                setNoteModal(prev => ({ ...prev, notes: res.data.results || res.data, loading: false }));
            }
        } catch (error) {
            toast.error("Failed to load notes");
        }
    };

    const handleAddNote = async () => {
        if (!noteModal.newNote.trim()) return;
        try {
            const res = await createB2BLeadNote({ lead: noteModal.lead.id, content: noteModal.newNote });
            if (res.status === 201) {
                toast.success("Note added");
                setNoteModal(prev => ({ ...prev, newNote: '', notes: [res.data, ...prev.notes] }));
            }
        } catch (error) {
            toast.error("Error adding note");
        }
    };

    const handleInlineUpdate = async (leadId, updatedFields) => {
        setIsUpdating(leadId);
        try {
            const res = await updateB2BLead(leadId, updatedFields);
            if (res.status === 200) {
                toast.success("Updated");
                fetchLeads();
            } else {
                toast.error("Failed to update");
            }
        } catch (error) {
            toast.error("Error updating lead");
        } finally {
            setIsUpdating(null);
        }
    };

    const handleInlineAddNote = async (leadId, content) => {
        if (!content.trim()) return;
        setIsUpdating(leadId);
        try {
            const res = await createB2BLeadNote({ lead: leadId, content });
            if (res.status === 201) {
                toast.success("Note added");
                fetchLeads();
            }
        } catch (error) {
            toast.error("Error adding note");
        } finally {
            setIsUpdating(null);
        }
    };

    const openLeadModal = async (mode = 'add', lead = null) => {
        setModalVisitingCardFile(null);
        setShowOptionalDetails(false);
        if (mode === 'edit' && lead) {
            openNotes(lead);
            setLeadFormData({
                shop_name: lead.shop_name || '',
                owner_name: lead.owner_name || '',
                phone: lead.phone || '',
                additional_numbers: lead.additional_numbers || '',
                area: lead.area || '',
                address_line1: lead.address_line1 || '',
                state_name: lead.state_name || lead.state || '',
                district_name: lead.district_name || lead.district || '',
                product_category: lead.product_category ? lead.product_category.map(c => c.id) : [],
                product_subcategory: lead.product_subcategory ? lead.product_subcategory.map(sc => sc.id) : (lead.product_sub_category ? lead.product_sub_category.map(sc => sc.id) : []),
                is_paid_dealer: lead.is_paid_dealer || false,
                is_urgent: lead.is_urgent || false,
                campaign: lead.campaign || ''
            });
            if (lead.state_name || lead.state) {
                try {
                    const res = await axios.get(`${districtApi}${lead.state_name || lead.state}`, { headers: { Authorization: `Token ${getUserToken()}` } });
                    if (res.status === 200) setFormDistricts(res.data);
                } catch (error) {
                    setFormDistricts([]);
                }
            } else {
                setFormDistricts([]);
            }
        } else {
            setLeadFormData({
                shop_name: '',
                owner_name: '',
                phone: '',
                additional_numbers: '',
                area: '',
                address_line1: '',
                state_name: '',
                district_name: '',
                product_category: [],
                product_subcategory: [],
                is_paid_dealer: false,
                is_urgent: false,
                campaign: filters.campaign || ''
            });
            setFormDistricts([]);
        }
        setLeadModal({ open: true, mode, lead });
    };

    const handleLeadFormSubmit = async () => {
        setLoading(true);
        try {
            let res;
            const payload = { ...leadFormData };

            let submitData;
            if (modalVisitingCardFile) {
                submitData = new FormData();
                Object.keys(payload).forEach(key => {
                    const val = payload[key];
                    if (Array.isArray(val)) {
                        val.forEach(item => submitData.append(key, item));
                    } else if (val !== null && val !== undefined) {
                        submitData.append(key, val);
                    }
                });
                submitData.append('visiting_card', modalVisitingCardFile);
            } else {
                submitData = payload;
            }

            if (leadModal.mode === 'edit') {
                if (payload.product_category && payload.product_category.length === 0) {
                    delete payload.product_category;
                }
                if (payload.product_subcategory && payload.product_subcategory.length === 0) {
                    delete payload.product_subcategory;
                }
                res = await updateB2BLead(leadModal.lead.id, submitData);
            } else {
                res = await createB2BLead(submitData);
            }

            if (res.status === 200 || res.status === 201) {
                toast.success(`Lead ${leadModal.mode === 'edit' ? 'updated' : 'created'} successfully`);
                setLeadModal({ open: false, mode: 'add', lead: null });
                setModalVisitingCardFile(null);
                fetchLeads();
            } else {
                toast.error(res.data?.detail || "Failed to process lead");
            }
        } catch (error) {
            toast.error("Error connecting to server");
        } finally {
            setLoading(false);
        }
    };

    const handleLeadStateChange = async (val) => {
        setLeadFormData(prev => ({ ...prev, state_name: val, district_name: '' }));
        if (val) {
            try {
                const res = await axios.get(`${districtApi}${val}`, { headers: { Authorization: `Token ${getUserToken()}` } });
                if (res.status === 200) setFormDistricts(res.data);
            } catch (error) {
                setFormDistricts([]);
            }
        } else {
            setFormDistricts([]);
        }
    };

    const handleDownload = async () => {
        const toastId = toast.info("Preparing download...", { autoClose: false });
        try {
            let query = '';
            if (filters.campaign) query += `campaign=${filters.campaign}&`;
            if (filters.state) query += `state=${filters.state}&`;
            if (filters.district) query += `district=${filters.district}&`;
            if (filters.product_category) query += `product_category=${filters.product_category}&`;
            if (filters.is_paid !== '') query += `is_paid_dealer=${filters.is_paid}&`;

            const res = await downloadB2BLeads(query);
            if (res.status === 200) {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `b2b_leads_${new Date().toISOString().split('T')[0]}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.update(toastId, { render: "Download started!", type: "success", autoClose: 3000 });
            } else {
                toast.update(toastId, { render: "Failed to download", type: "error", autoClose: 3000 });
            }
        } catch (err) {
            console.error("Error downloading leads:", err);
            toast.update(toastId, { render: "Failed to download leads", type: "error", autoClose: 3000 });
        }
    };

    const openWhatsapp = (phone) => {
        const url = `https://wa.me/${(phone || '').replace(/\D/g, '')}`;
        window.open(url, "_blank");
    };

    const openLeadDetail = (item) => {
        setSelectedLeadForDetail(item);
    };

    const openAssignModal = (leadIds) => {
        setAssignForm({ sales_person: '', remarks: '' });
        setAssignModal({ open: true, leadIds });
    };

    const closeAssignModal = () => {
        setAssignModal({ open: false, leadIds: [] });
        setAssignForm({ sales_person: '', remarks: '' });
    };

    const handleAssignLeads = async () => {
        if (!assignForm.sales_person) {
            toast.error("Please select a salesperson");
            return;
        }
        setAssigning(true);
        try {
            const res = await bulkAssignB2BLeads({
                lead_ids: assignModal.leadIds,
                sales_person: assignForm.sales_person,
                remarks: assignForm.remarks || undefined,
            });
            if (res.status === 200 || res.status === 201) {
                const d = res.data || {};
                toast.success(`Assigned ${d.assigned_count || 0} leads · ${d.reassigned?.length || 0} reassigned · ${d.skipped_same_salesperson?.length || 0} skipped`);
                closeAssignModal();
                setSelectedLeadIds([]);
                fetchLeads();
                fetchStats();
            } else {
                toast.error(res.data?.detail || "Failed to assign leads");
            }
        } catch (error) {
            toast.error("Error assigning leads");
        } finally {
            setAssigning(false);
        }
    };

    const toggleLeadSelection = (leadId) => {
        setSelectedLeadIds(prev => prev.includes(leadId) ? prev.filter(id => id !== leadId) : [...prev, leadId]);
    };

    const allPageSelected = data.results?.length > 0 && data.results.every(item => selectedLeadIds.includes(item.id));

    const togglePageSelection = () => {
        if (allPageSelected) {
            setSelectedLeadIds(prev => prev.filter(id => !data.results.some(item => item.id === id)));
        } else {
            setSelectedLeadIds(prev => Array.from(new Set([...prev, ...(data.results || []).map(item => item.id)])));
        }
    };

    const headers = [
        { label: <input type="checkbox" checked={allPageSelected} onChange={togglePageSelection} className="accent-primary" />, key: "select", width: "34px" },
        { label: "Date", key: "created_at", width: "85px" },
        { label: "Shop/Owner", key: "shop_owner" },
        { label: "Phone", key: "phone", width: "110px" },
        { label: "Assigned To", key: "assigned_to", width: "145px" },
        { label: "Product Category", key: "product_category", width: "130px" },
        { label: "Last Outcome", key: "last_outcome", width: "120px" },
        { label: "District/State", key: "location" },
        { label: "Last Order Date", key: "last_order_date", width: "110px" },
        { label: "Flags", key: "flags", width: "95px" },
        { label: "Actions", key: "actions", width: "160px" },
    ];

    const rows = (data.results || []).map((item, index) => ({
        select: (
            <input
                type="checkbox"
                checked={selectedLeadIds.includes(item.id)}
                onChange={() => toggleLeadSelection(item.id)}
                className="accent-primary"
            />
        ),
        created_at: (
            <span className="text-[10px] text-gray-500 font-medium">
                {formatDateMonth(item.created_at)}
            </span>
        ),
        shop_owner: (
            <div className="flex flex-col gap-1 w-full min-w-[125px]">
                <input
                    key={`shop-${item.id}`}
                    type="text"
                    className="border border-gray-200 rounded p-1 text-xs w-full font-bold focus:ring-1 focus:ring-primary outline-none bg-gray-50/50 focus:bg-white transition-all"
                    defaultValue={item.shop_name || ""}
                    placeholder="Shop name"
                    onBlur={(e) => {
                        if (e.target.value !== (item.shop_name || "") && e.target.value) {
                            handleInlineUpdate(item.id, { shop_name: e.target.value });
                        }
                    }}
                />
                <input
                    key={`owner-${item.id}`}
                    type="text"
                    className="border border-gray-200 rounded p-1 text-[11px] w-full focus:ring-1 focus:ring-primary outline-none bg-gray-50/50 focus:bg-white transition-all mt-0.5"
                    defaultValue={item.owner_name || ""}
                    placeholder="Owner name"
                    onBlur={(e) => {
                        if (e.target.value !== (item.owner_name || "")) {
                            handleInlineUpdate(item.id, { owner_name: e.target.value });
                        }
                    }}
                />
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openLeadDetail(item); }}
                    className="text-[10px] text-primary hover:underline text-left mt-0.5"
                >
                    View Details
                </button>
            </div>
        ),
        phone: (
            <div className="flex flex-col gap-1 items-start">
                {item.phone ? (
                    <button
                        type="button"
                        onClick={() => openLeadDetail(item)}
                        className="text-sm font-bold text-primary hover:underline text-left"
                    >
                        {item.phone}
                    </button>
                ) : (
                    <span className="text-sm text-gray-400">—</span>
                )}
                <div className="mt-0.5">
                    {item.visiting_card ? (
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => setViewingCardUrl(item.visiting_card)}
                                className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-50 border border-gray-200 rounded text-[9px] font-bold text-gray-600 hover:bg-gray-100 transition-all whitespace-nowrap"
                            >
                                <Eye size={10} /> View Card
                            </button>
                            <label className="bg-white border rounded p-0.5 shadow-sm cursor-pointer hover:bg-gray-50 flex items-center justify-center">
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handleVisitingCardUpload(item.id, e.target.files[0])}
                                />
                                <svg className="w-2.5 h-2.5 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
                                </svg>
                            </label>
                        </div>
                    ) : (
                        <label className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-50 border border-dashed border-gray-300 rounded text-[9px] font-bold text-gray-500 hover:bg-gray-100 hover:border-gray-400 cursor-pointer transition-all whitespace-nowrap">
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleVisitingCardUpload(item.id, e.target.files[0])}
                            />
                            <span>+ Visiting Card</span>
                        </label>
                    )}
                </div>
            </div>
        ),
        assigned_to: item.assigned_to ? (
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-1 max-w-[135px]">
                <span className="w-5 h-5 rounded-full bg-primary text-white text-[10px] font-black flex items-center justify-center shrink-0">
                    {(item.assigned_to.name || '?').charAt(0).toUpperCase()}
                </span>
                <span className="text-[11px] font-bold text-emerald-800 truncate">{item.assigned_to.name}</span>
            </div>
        ) : (
            <span className="text-[11px] italic text-gray-400">Unassigned</span>
        ),
        product_category: (
            <div className="flex flex-wrap gap-1">
                {(item.product_category || item.product_categories || item.category || []).slice(0, 2).map(c => (
                    <span key={c.id} className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded-full text-[10px] font-bold">
                        {c.name}
                    </span>
                ))}
                {!(item.product_category || item.product_categories || item.category || []).length && <span className="text-[10px] text-gray-400">—</span>}
            </div>
        ),
        flags: (
            <div className="flex flex-col gap-1.5 items-start">
                {item.is_paid && <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-black">₹ Paid</span>}
                <button
                    type="button"
                    onClick={async (e) => {
                        e.stopPropagation();
                        const toastId = toast.loading("Updating urgency...");
                        try {
                            const res = await updateB2BLead(item.id, { is_urgent: !item.is_urgent });
                            if (res.status === 200) {
                                toast.update(toastId, { render: `Lead marked as ${!item.is_urgent ? 'Urgent' : 'Normal'}`, type: "success", isLoading: false, autoClose: 2000 });
                                fetchLeads();
                            } else {
                                toast.update(toastId, { render: "Failed to update urgency", type: "error", isLoading: false, autoClose: 2000 });
                            }
                        } catch (err) {
                            toast.update(toastId, { render: "Error updating urgency", type: "error", isLoading: false, autoClose: 2000 });
                        }
                    }}
                    className={`px-1.5 py-0.5 rounded-full text-[10px] font-black transition-all flex items-center gap-0.5 ${
                        item.is_urgent 
                            ? 'bg-amber-100 text-amber-700 border border-amber-200 hover:bg-amber-200' 
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
                    }`}
                    title={item.is_urgent ? "Mark Normal" : "Mark Urgent"}
                >
                    ⚡ {item.is_urgent ? "Urgent" : "Urgent?"}
                </button>
            </div>
        ),
        last_outcome: (
            <div className="flex flex-col gap-1">
                <div>
                    {outcomeBadge(item.last_outcome || 'new')}
                    {item.last_outcome_comment && (
                        <div className="text-[10px] text-gray-500 italic max-w-[120px] truncate" title={item.last_outcome_comment}>
                            {item.last_outcome_comment}
                        </div>
                    )}
                </div>
                {item.next_follow_up?.follow_up_at && (
                    <div className={`text-[10px] font-bold ${isOverdue(item.next_follow_up.follow_up_at) ? 'text-rose-600' : isToday(item.next_follow_up.follow_up_at) ? 'text-amber-600' : 'text-gray-500'}`} title="Next Follow-up">
                        📅 {new Date(item.next_follow_up.follow_up_at).toLocaleDateString([], { dateStyle: 'medium' })}
                    </div>
                )}
            </div>
        ),

        location: (
            <div className="text-xs text-gray-600">
                <div>{item.district_name || item.district || '—'} / {item.state_name || item.state || '—'}</div>
                {item.area && <div className="text-[10px] text-gray-400">{item.area}</div>}
            </div>
        ),
        last_order_date: (
            <span className={`px-2 py-1 rounded text-[10px] font-bold ${item.is_paid_dealer ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                {item.is_paid_dealer ? item.last_order_date || 'Paid' : 'Unpaid'}
            </span>
        ),
        actions: (
            <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                <button
                    onClick={() => setLogModal({ open: true, lead: item })}
                    className="p-1.5 bg-primary text-white rounded-full hover:bg-primary/90 transition-colors"
                    title="Log Interaction"
                >
                    <PhoneCall size={14} />
                </button>
                <button
                    onClick={() => { setIsUpdating(item.id); fetchLeads().finally(() => setIsUpdating(null)); }}
                    className="p-1.5 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
                    disabled={isUpdating === item.id}
                    title="Refresh"
                >
                    <BiRefresh size={14} className={isUpdating === item.id ? "animate-spin" : ""} />
                </button>
            </div>
        ),
        _original: item
    }));

    const hasActiveFilters = filters.campaign || filters.state || filters.district || filters.product_category || filters.product_subcategory || filters.is_paid !== '' || filters.sales_person || filters.assigned !== '' || filters.outcome || filters.is_urgent !== '' || filters.start_date || filters.end_date;

    return (
        <div className="space-y-3">
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-3 border border-emerald-100 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                        <Breadcrumbs items={[{ name: 'Home', link: '/dashboard' }, { name: 'Leads', link: '/leads-management' }, { name: 'B2B' }, { name: 'Leads' }]} />
                        <div className="flex items-center gap-3 mt-1">
                            <h1 className="text-xl font-bold text-gray-800">B2B Leads</h1>
                            <p className="text-xs text-gray-500 font-medium bg-white px-2 py-0.5 rounded-full border border-emerald-100">{data.count} Leads</p>
                        </div>
                    </div>
                    <div className="flex gap-2 items-center">
                        <div className="flex-1 md:flex-none md:w-[250px]">
                            <SearchBar placeholder="Search leads..." onSearch={(val) => handleFilterChange('search', val)} value={filters.search} compact />
                        </div>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border transition-all ${hasActiveFilters ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-300 hover:border-primary hover:text-primary'}`}
                        >
                            <BiFilterAlt size={14} /> Filters {hasActiveFilters && <span className="bg-white/20 px-1.5 rounded-full text-[9px]">ON</span>}
                        </button>

                        <Button variant="primary" onClick={() => openLeadModal('add')} icon={<BiPlus />} className="bg-primary text-white py-1.5 text-xs">Add Lead</Button>
                        <Button variant="primary" onClick={() => setUploadModal(true)} icon={<FiUpload />} className="bg-primary text-white py-1.5 text-xs">Bulk Upload</Button>
                    </div>
                </div>
            </div>

            {/* Collapsible Inline Filters Panel */}
            {showFilters && (
                <div className="bg-white rounded-xl p-4 border border-gray-200/60 shadow-sm transition-all space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b">
                        <div>
                            <h2 className="text-sm font-black text-gray-800">Filters</h2>
                            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Refine your leads</p>
                        </div>
                        <button
                            onClick={() => resetFilters()}
                            className="text-[10px] font-black text-rose-600 hover:text-rose-700 uppercase tracking-wider"
                        >
                            Reset All
                        </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                        <Dropdown
                            label="Campaign"
                            options={campaigns.map(c => ({ label: c.name, value: c.id }))}
                            value={filters.campaign}
                            onChange={(val) => handleFilterChange('campaign', val)}
                            placeholder="All Campaigns"
                            needSearch
                            compact
                        />

                        <Dropdown
                            label="State"
                            options={(statesOfIndia || []).map(s => ({ label: s, value: s }))}
                            value={filters.state}
                            onChange={handleStateChange}
                            needSearch
                            placeholder="Region"
                            compact
                        />

                        <Dropdown
                            label="District"
                            options={districts.map(d => ({ label: d.name, value: d.id }))}
                            value={filters.district}
                            onChange={(val) => handleFilterChange('district', val)}
                            needSearch
                            placeholder="District"
                            disabled={!filters.state}
                            compact
                        />

                        <Dropdown
                            label="Product Category"
                            options={categories.map(c => ({ label: c.name, value: c.id }))}
                            value={filters.product_category}
                            onChange={(val) => handleFilterChange('product_category', val)}
                            placeholder="All Categories"
                            needSearch
                            compact
                        />

                        <Dropdown
                            label="Product Sub-Category"
                            options={subcategories
                                .filter(sc => {
                                    const catId = sc.category?.id || sc.category;
                                    return !filters.product_category || String(catId) === String(filters.product_category);
                                })
                                .map(sc => ({ label: sc.name, value: sc.id }))}
                            value={filters.product_subcategory}
                            onChange={(val) => handleFilterChange('product_subcategory', val)}
                            placeholder="All Sub-Categories"
                            needSearch
                            compact
                        />

                        <div className="form-control w-full">
                            <label className="block text-xs font-bold text-gray-700 mb-1">Product</label>
                            <MultiAsyncDropdown
                                placeholder="Search products..."
                                apiCallFunction={async (input) => {
                                    const response = await searchProductWithAkaName(input);
                                    const raw = response?.data?.results ?? response?.data;
                                    return (Array.isArray(raw) ? raw : []).map(pro => ({
                                        value: pro.id,
                                        label: pro.aka_name ?? pro.product_name ?? "Product",
                                    }));
                                }}
                                value={filters.product}
                                onChange={(val) => handleFilterChange('product', val)}
                                needSearch
                                compact
                                menuPortalTarget={document.body}
                            />
                        </div>

                        <Dropdown
                            label="Salesperson"
                            options={staffOptions}
                            value={filters.sales_person}
                            onChange={(val) => handleFilterChange('sales_person', val)}
                            placeholder="All Salespeople"
                            needSearch
                            compact
                        />


                        <div className="space-y-1.5">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Assignment</label>
                            <div className="flex bg-gray-100 p-1 rounded-lg">
                                {[
                                    { label: 'All', value: '' },
                                    { label: 'Assigned', value: 'true' },
                                    { label: 'Unassigned', value: 'false' },
                                ].map(option => (
                                    <button
                                        key={option.label}
                                        onClick={() => handleFilterChange('assigned', option.value)}
                                        className={`flex-1 text-[10px] font-bold py-1 px-1.5 rounded-md transition-all ${filters.assigned === option.value ? 'bg-white shadow-sm text-primary' : 'text-gray-500 hover:text-gray-700'}`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Dealer Status</label>
                            <div className="flex bg-gray-100 p-1 rounded-lg">
                                <button
                                    onClick={() => handleFilterChange('is_paid', '')}
                                    className={`flex-1 text-[10px] font-bold py-1 px-1.5 rounded-md transition-all ${filters.is_paid === '' ? 'bg-white shadow-sm text-primary' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    All
                                </button>
                                <button
                                    onClick={() => handleFilterChange('is_paid', 'true')}
                                    className={`flex-1 text-[10px] font-bold py-1 px-1.5 rounded-md transition-all ${filters.is_paid === 'true' ? 'bg-white shadow-sm text-emerald-600' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    Paid
                                </button>
                                <button
                                    onClick={() => handleFilterChange('is_paid', 'false')}
                                    className={`flex-1 text-[10px] font-bold py-1 px-1.5 rounded-md transition-all ${filters.is_paid === 'false' ? 'bg-white shadow-sm text-red-600' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    Unpaid
                                </button>
                            </div>
                        </div>

                        <div className="flex items-end">
                            <label className="flex items-center justify-between gap-3 p-2.5 w-full rounded-xl border border-amber-100 bg-amber-50/60 h-[38px] cursor-pointer">
                                <span className="text-xs font-black text-amber-700 uppercase tracking-wide">Urgent only</span>
                                <input
                                    type="checkbox"
                                    checked={filters.is_urgent === 'true'}
                                    onChange={(e) => handleFilterChange('is_urgent', e.target.checked ? 'true' : '')}
                                    className="accent-primary"
                                />
                            </label>
                        </div>

                        {/* Date Range Picker */}
                        <div className="space-y-1.5 sm:col-span-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Date Range</label>
                            <DateRangePicker
                                compact
                                placeholder="Select date range"
                                value={{ start: filters.start_date, end: filters.end_date }}
                                onChange={({ start, end }) => {
                                    setFilters(prev => ({ ...prev, start_date: start || '', end_date: end || '' }));
                                    setCurrentPage(1);
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                {statsLoading ? (
                    [1, 2, 3, 4, 5].map(i => (
                        <div key={i} className="animate-pulse bg-white rounded-xl h-16 border border-gray-100"></div>
                    ))
                ) : (
                    <>
                        {stats.map((stat, idx) => (
                            <div key={idx} className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm flex items-center justify-between group hover:shadow-md transition-shadow">
                                <div className="space-y-0.5">
                                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{stat.title}</p>
                                    <div className="flex items-baseline gap-2">
                                        <h3 className="text-xl font-black text-gray-800">{stat.value}</h3>
                                        <span className={`text-[9px] font-bold px-1 py-0.5 rounded ${idx === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-50 text-blue-600'}`}>
                                            {stat.percentage}%
                                        </span>
                                    </div>
                                </div>
                                <div className={`p-2 rounded-lg ${idx === 0 ? 'bg-blue-50 text-blue-600' : idx === 1 ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-50 text-gray-600'}`}>
                                    {idx === 0 ? <FiUsers size={18} /> : idx === 1 ? <FiDollarSign size={18} /> : <FiFileText size={18} />}
                                </div>
                            </div>
                        ))}
                        <div className="bg-white rounded-xl p-3 border border-amber-100 shadow-sm flex items-center justify-between">
                            <div>
                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Unassigned</p>
                                <h3 className="text-xl font-black text-gray-800">{workflowCounts.unassigned}</h3>
                            </div>
                            <UserPlus size={18} className="text-amber-600" />
                        </div>
                        <div className="bg-white rounded-xl p-3 border border-rose-100 shadow-sm flex items-center justify-between">
                            <div>
                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Urgent</p>
                                <h3 className="text-xl font-black text-gray-800">{workflowCounts.urgent}</h3>
                            </div>
                            <span className="text-lg">⚡</span>
                        </div>
                    </>
                )}
            </div>

            {selectedLeadIds.length > 0 && (
                <div className="bg-white border border-emerald-100 shadow-sm rounded-xl px-4 py-3 flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="text-sm font-black text-gray-800">
                        {selectedLeadIds.length} leads selected
                    </div>
                    <button
                        onClick={() => openAssignModal(selectedLeadIds)}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-xs font-black shadow-sm hover:bg-primary/90"
                    >
                        <UserPlus size={14} /> Assign to salesperson
                    </button>
                </div>
            )}

            {/* Table */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                {loading ? (
                    <div className="flex justify-center py-20"><CustomLoading size={80} /></div>
                ) : (
                    <>
                        <Table headers={headers} rows={rows} />
                        {data.results?.length === 0 && (
                            <div className="text-center py-10 text-gray-500">
                                No leads found matching your criteria.
                            </div>
                        )}
                        <div className="p-4 border-t">
                            <Pagination
                                currentPage={currentPage}
                                totalEntries={data.count}
                                onPageChange={setCurrentPage}
                                pageSize={pageSize}
                                data={data}
                            />
                        </div>
                    </>
                )}
            </div>


            {/* Lead Detail Drawer */}
            {selectedLeadForDetail && (
                <B2BLeadDetail
                    lead={selectedLeadForDetail}
                    onAssignRequest={(leadId) => openAssignModal([leadId])}
                    onUpdated={() => {
                        fetchLeads();
                        fetchStats();
                    }}
                    onClose={() => setSelectedLeadForDetail(null)}
                />
            )}

            <ShowModal
                isOpen={assignModal.open}
                onRequestClose={closeAssignModal}
                title={`Assign ${assignModal.leadIds.length} lead${assignModal.leadIds.length === 1 ? '' : 's'}`}
                modalWidth="420px"
                componentToPass={
                    <div className="space-y-4">
                        <Dropdown
                            label="Salesperson"
                            options={staffOptions}
                            value={assignForm.sales_person}
                            onChange={(val) => setAssignForm(prev => ({ ...prev, sales_person: val }))}
                            placeholder="Select salesperson"
                            needSearch
                        />
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                            <textarea
                                value={assignForm.remarks}
                                onChange={(e) => setAssignForm(prev => ({ ...prev, remarks: e.target.value }))}
                                rows={3}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                                placeholder="Optional assignment note"
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <button onClick={closeAssignModal} className="px-4 py-2 text-xs font-bold border border-gray-200 rounded-lg text-gray-600">Cancel</button>
                            <button
                                onClick={handleAssignLeads}
                                disabled={assigning}
                                className="px-5 py-2 text-xs font-black bg-primary text-white rounded-lg disabled:bg-gray-300"
                            >
                                {assigning ? 'Assigning...' : 'Assign'}
                            </button>
                        </div>
                    </div>
                }
            />

            <B2BLogInteractionModal
                isOpen={logModal.open}
                lead={logModal.lead}
                onClose={() => setLogModal({ open: false, lead: null })}
                onSuccess={() => {
                    fetchLeads();
                    fetchStats();
                }}
            />

            {/* JSON View Modal */}
            <ShowModal
                isOpen={jsonModal.open}
                onRequestClose={() => setJsonModal({ open: false, data: null })}
                contentLabel="View Form Data"
                modalWidth="600px"
                componentToPass={
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                <BiCodeAlt /> Form Response Data
                            </h2>
                            <button onClick={() => setJsonModal({ open: false, data: null })} className="text-gray-400 hover:text-gray-600">✕</button>
                        </div>
                        <div className="bg-gray-900 rounded-xl p-6 overflow-auto max-h-[500px]">
                            <pre className="text-emerald-400 text-sm font-mono leading-relaxed">
                                {JSON.stringify(jsonModal.data, null, 4)}
                            </pre>
                        </div>
                        {(!jsonModal.data || Object.keys(jsonModal.data).length === 0) && (
                            <p className="text-center text-gray-500 py-4">No additional form data available.</p>
                        )}
                    </div>
                }
            />
            {/* Lead Add/Edit Modal */}
            <ShowModal
                isOpen={leadModal.open}
                isSidebar={false}
                onRequestClose={() => setLeadModal({ ...leadModal, open: false })}
                title={leadModal.mode === 'edit' ? `Edit Lead: ${leadFormData.shop_name}` : 'Add New Lead'}
                modalWidth="480px"
                componentToPass={
                    <Formik
                        initialValues={leadFormData}
                        enableReinitialize
                        onSubmit={handleLeadFormSubmit}
                    >
                        {({ setFieldValue }) => (
                            <Form className="flex flex-col bg-white">
                                <div className="flex-1 max-h-[60vh] overflow-y-auto p-4 space-y-4">
                                    {/* Highlighted Phone Section */}
                                    <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-3.5 space-y-2 shadow-sm">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] font-black text-amber-700 uppercase tracking-widest bg-amber-100 px-2 py-0.5 rounded-full">Primary Action</span>
                                            <span className="text-[10px] text-red-500 font-bold">* Required</span>
                                        </div>
                                        <InputField
                                            label="Phone Number"
                                            name="phone"
                                            required
                                            placeholder="Enter 10-digit mobile number"
                                            className="w-full font-bold text-sm bg-white"
                                            onChange={(e) => {
                                                const sanitized = sanitizeMobile(e.target.value);
                                                e.target.value = sanitized;
                                                setFieldValue('phone', sanitized);
                                                setLeadFormData(prev => ({ ...prev, phone: sanitized }));
                                            }}
                                        />
                                    </div>

                                    {/* Product Subcategories (Moved outside) */}
                                    <MultiSelectDropDown
                                        label={`Product Subcategories ${leadFormData.product_subcategory?.length > 0 ? `(${leadFormData.product_subcategory.map(id => subcategories.find(sc => sc.id === id)?.name).filter(Boolean).join(", ")})` : ''}`}
                                        placeholder="Select subcategories"
                                        options={subcategories
                                            .filter(sc => {
                                                const catId = sc.category?.id || sc.category;
                                                return leadFormData.product_category.length === 0 || leadFormData.product_category.includes(Number(catId));
                                            })
                                            .map(sc => ({ label: sc.name, value: sc.id }))}
                                        value={leadFormData.product_subcategory || []}
                                        onChange={(val) => {
                                            setFieldValue('product_subcategory', val);
                                            setLeadFormData(prev => ({ ...prev, product_subcategory: val }));
                                        }}
                                    />

                                    {/* Drag & Drop Visiting Card Dropzone */}
                                    <div className="space-y-1.5">
                                        <label className="block text-xs font-bold text-gray-700">Visiting Card Image</label>
                                        {leadModal.mode === 'edit' && leadModal.lead?.visiting_card && (
                                            <div className="mb-2">
                                                <img
                                                    src={leadModal.lead.visiting_card}
                                                    alt="Current Visiting Card"
                                                    className="w-32 h-20 object-cover rounded-lg border shadow-sm cursor-pointer hover:opacity-90 transition-all"
                                                    onClick={() => setViewingCardUrl(leadModal.lead.visiting_card)}
                                                />
                                            </div>
                                        )}
                                        <div
                                            className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all ${
                                                modalVisitingCardFile
                                                    ? 'border-emerald-500 bg-emerald-50/30'
                                                    : 'border-gray-300 hover:border-primary bg-gray-50/50 hover:bg-gray-50'
                                            }`}
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                                                    setModalVisitingCardFile(e.dataTransfer.files[0]);
                                                }
                                            }}
                                            onClick={() => document.getElementById('modal-card-upload').click()}
                                        >
                                            <input
                                                type="file"
                                                id="modal-card-upload"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={(e) => {
                                                    if (e.target.files && e.target.files[0]) {
                                                        setModalVisitingCardFile(e.target.files[0]);
                                                    }
                                                }}
                                            />
                                            <div className="flex flex-col items-center gap-1">
                                                <FiUpload className={`w-5 h-5 ${modalVisitingCardFile ? 'text-emerald-500' : 'text-gray-400'}`} />
                                                <span className="text-xs font-bold text-gray-600">
                                                    {modalVisitingCardFile ? modalVisitingCardFile.name : 'Drag & Drop or Click to Upload'}
                                                </span>
                                                <span className="text-[10px] text-gray-400">Supported: JPG, PNG, WEBP</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Collapsible Trigger for Optional Details */}
                                    <div className="pt-2">
                                        <button
                                            type="button"
                                            onClick={() => setShowOptionalDetails(!showOptionalDetails)}
                                            className="flex items-center justify-between w-full px-4 py-2.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl text-xs font-bold text-gray-750 transition-all select-none outline-none focus:outline-none"
                                        >
                                            <span className="flex items-center gap-1.5">
                                                📝 {showOptionalDetails ? "Hide Optional Details" : "Show Optional Details"}
                                            </span>
                                            <span className="text-[10px] text-gray-400">{showOptionalDetails ? "▲" : "▼"}</span>
                                        </button>
                                    </div>

                                    {/* Optional Details Section */}
                                    {showOptionalDetails && (
                                        <div className="space-y-3.5 pt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                                            {/* Urgency Checkbox (Moved inside Optional Details) */}
                                            <div className="flex items-center justify-between py-1">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={leadFormData.is_urgent}
                                                        onChange={(e) => {
                                                            setFieldValue('is_urgent', e.target.checked);
                                                            setLeadFormData(prev => ({ ...prev, is_urgent: e.target.checked }));
                                                        }}
                                                        className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300 accent-primary"
                                                    />
                                                    <span className="text-xs font-bold text-gray-750 flex items-center gap-1">⚡ Mark as Urgent</span>
                                                </label>
                                            </div>

                                            <InputField
                                                label="Shop Name"
                                                name="shop_name"
                                                placeholder="Enter shop name"
                                                onChange={(e) => {
                                                    setFieldValue('shop_name', e.target.value);
                                                    setLeadFormData(prev => ({ ...prev, shop_name: e.target.value }));
                                                }}
                                            />
                                            <InputField
                                                label="Owner Name"
                                                name="owner_name"
                                                placeholder="Enter owner name"
                                                onChange={(e) => {
                                                    setFieldValue('owner_name', e.target.value);
                                                    setLeadFormData(prev => ({ ...prev, owner_name: e.target.value }));
                                                }}
                                            />
                                            <InputField
                                                label="Additional Numbers"
                                                name="additional_numbers"
                                                placeholder="Name:Number, Name:Number"
                                                onChange={(e) => {
                                                    setFieldValue('additional_numbers', e.target.value);
                                                    setLeadFormData(prev => ({ ...prev, additional_numbers: e.target.value }));
                                                }}
                                            />
                                            <InputField
                                                label="Area"
                                                name="area"
                                                placeholder="Enter area"
                                                onChange={(e) => {
                                                    setFieldValue('area', e.target.value);
                                                    setLeadFormData(prev => ({ ...prev, area: e.target.value }));
                                                }}
                                            />
                                            <Dropdown
                                                label="Campaign"
                                                placeholder="Select Campaign"
                                                options={campaigns.map(c => ({ label: c.name, value: c.id }))}
                                                value={leadFormData.campaign}
                                                onChange={(val) => {
                                                    setFieldValue('campaign', val);
                                                    setLeadFormData(prev => ({ ...prev, campaign: val }));
                                                }}
                                                compact
                                            />
                                            <Dropdown
                                                label={`State ${leadFormData.state_name ? `(${leadFormData.state_name})` : ''}`}
                                                placeholder="Select State"
                                                options={(statesOfIndia || []).map(s => ({ label: s, value: s }))}
                                                value={leadFormData.state_name}
                                                onChange={(val) => {
                                                    setFieldValue('state_name', val);
                                                    handleLeadStateChange(val);
                                                }}
                                                compact
                                            />
                                            <Dropdown
                                                label={`District ${leadFormData.district_name ? `(${leadFormData.district_name})` : ''}`}
                                                placeholder="Select District"
                                                options={formDistricts.map(d => ({ label: d.name, value: d.name }))}
                                                value={leadFormData.district_name}
                                                onChange={(val) => {
                                                    setFieldValue('district_name', val);
                                                    setLeadFormData(prev => ({ ...prev, district_name: val }));
                                                }}
                                                compact
                                                needSearch
                                            />
                                            <InputField
                                                label="Address"
                                                name="address_line1"
                                                placeholder="Full address"
                                                onChange={(e) => {
                                                    setFieldValue('address_line1', e.target.value);
                                                    setLeadFormData(prev => ({ ...prev, address_line1: e.target.value }));
                                                }}
                                            />
                                            <MultiSelectDropDown
                                                label={`Product Categories ${leadFormData.product_category.length > 0 ? `(${leadFormData.product_category.map(id => categories.find(c => c.id === id)?.name).filter(Boolean).join(", ")})` : ''}`}
                                                placeholder="Select categories"
                                                options={categories.map(c => ({ label: c.name, value: c.id }))}
                                                value={leadFormData.product_category}
                                                onChange={(val) => {
                                                    setFieldValue('product_category', val);
                                                    setLeadFormData(prev => ({ ...prev, product_category: val }));
                                                }}
                                            />
                                        </div>
                                    )}

                                    {/* Order History Section (Only for Edit with User ID) */}
                                    {leadModal.mode === 'edit' && leadModal.lead?.user_id && (
                                        <div className="p-4 bg-white">
                                            <div className="flex items-center gap-2 mb-4">
                                                <div className="h-4 w-1 bg-primary rounded-full"></div>
                                                <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Order History</h3>
                                            </div>
                                            <UserOrderHistory user_id={leadModal.lead?.user_id} showTopItems={false} />
                                        </div>
                                    )}
                                </div>

                                {/* Sticky Action Footer */}
                                <div className="sticky bottom-0 border-t p-4 bg-white flex justify-end gap-3 shrink-0 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-20">
                                    <Button variant="outline" className="text-[11px] py-2 px-5 h-auto font-bold uppercase tracking-wide border-gray-200" onClick={() => setLeadModal({ ...leadModal, open: false })}>Cancel</Button>
                                    <Button variant="primary" className="bg-primary text-white text-[11px] py-2 px-8 h-auto font-bold uppercase tracking-widest shadow-lg shadow-primary/20 transition-all hover:translate-y-[-1px]" onClick={handleLeadFormSubmit}>
                                        {leadModal.mode === 'edit' ? "Update Lead" : "Save Lead"}
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                }
            />
            {/* Bulk Upload Modal */}
            <ShowModal
                isOpen={uploadModal}
                onRequestClose={() => setUploadModal(false)}
                contentLabel="Bulk Upload Leads"
                modalWidth="1000px"
                componentToPass={
                    <div className="flex flex-col max-h-[90vh]">
                        <div className="flex-1 overflow-y-auto p-0 bg-gray-50/30">
                            <B2BLeadUpload isModal={true} onUploadSuccess={() => { setUploadModal(false); fetchLeads(); }} />
                        </div>
                    </div>
                }
            />
            {/* Visiting Card Lightbox Modal */}
            {viewingCardUrl && (
                <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4" onClick={() => setViewingCardUrl(null)}>
                    <div className="relative max-w-3xl max-h-[90vh] bg-white rounded-2xl p-2 shadow-2xl animate-in fade-in zoom-in-95 duration-150" onClick={e => e.stopPropagation()}>
                        <button
                            onClick={() => setViewingCardUrl(null)}
                            className="absolute -top-3 -right-3 bg-white border shadow-md text-gray-500 hover:text-gray-800 rounded-full p-1.5 transition-all active:scale-95 z-10"
                        >
                            <BiX size={20} />
                        </button>
                        <img
                            src={viewingCardUrl}
                            alt="Visiting Card Preview"
                            className="max-w-full max-h-[80vh] rounded-xl object-contain"
                        />
                    </div>
                </div>
            )}
        </div >
    );
};

export default B2BLeads;
